import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Gauge } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supplyService } from '@/services/supplyService';
import { invalidateAllSupplyTableCaches } from '@/utils/supplyTableCache';

export function SupplyIdealCoverageSettingsCard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canEdit = user?.role?.id === 1;
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(false);

  const settings = useQuery({
    queryKey: ['supply-organization-settings'],
    queryFn: () => supplyService.getOrganizationSettings(),
  });

  const enabled = settings.data?.low_coverage_enabled === true;
  const savedDays = settings.data?.ideal_coverage_days;

  useEffect(() => {
    setDraft(typeof savedDays === 'number' ? String(savedDays) : '');
  }, [savedDays]);

  const patch = useMutation({
    mutationFn: (payload: {
      low_coverage_enabled?: boolean;
      ideal_coverage_days?: number | null;
    }) => supplyService.updateOrganizationSettings(payload),
    onSuccess: () => {
      setError(false);
      void queryClient.invalidateQueries({ queryKey: ['supply-organization-settings'] });
      // Solo al guardar / activar: limpia caché de sesión para la próxima carga.
      // No recalcula filas en vivo en Orden sugerido.
      invalidateAllSupplyTableCaches();
    },
  });

  const parseDraftDays = (): number | null => {
    const trimmed = draft.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 1) return null;
    return parsed;
  };

  const saveDays = () => {
    const parsed = parseDraftDays();
    if (draft.trim() !== '' && parsed == null) {
      setError(true);
      return;
    }
    setError(false);
    patch.mutate({
      ideal_coverage_days: parsed,
      ...(enabled && parsed == null ? { low_coverage_enabled: false } : {}),
    });
  };

  const toggleEnabled = (next: boolean) => {
    if (!next) {
      setError(false);
      patch.mutate({ low_coverage_enabled: false });
      return;
    }
    const parsed = parseDraftDays() ?? (typeof savedDays === 'number' && savedDays >= 1 ? savedDays : null);
    if (parsed == null) {
      setError(true);
      return;
    }
    setError(false);
    patch.mutate({
      low_coverage_enabled: true,
      ideal_coverage_days: parsed,
    });
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Gauge className="w-5 h-5 text-stocker-primary" />
          {t('settings.supplyIdealCoverage.title')}
        </CardTitle>
        <CardDescription>{t('settings.supplyIdealCoverage.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {settings.isError ? (
          <p className="text-sm text-red-600">{t('settings.saveError')}</p>
        ) : settings.isLoading ? (
          <p className="text-sm text-gray-500">{t('common.loading')}</p>
        ) : (
          <div className="space-y-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 rounded border-gray-300 text-stocker-primary focus:ring-stocker-primary"
                checked={enabled}
                disabled={!canEdit || patch.isPending}
                onChange={(e) => toggleEnabled(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {t('settings.supplyIdealCoverage.enableLabel')}
                </span>
                <span className="block text-xs text-gray-500">
                  {enabled
                    ? t('settings.supplyIdealCoverage.onHint', {
                        days: typeof savedDays === 'number' ? savedDays : '—',
                      })
                    : t('settings.supplyIdealCoverage.offHint')}
                </span>
              </span>
            </label>

            <div className="flex flex-wrap items-end gap-3">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-gray-800">
                  {t('settings.supplyIdealCoverage.label')}
                </span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  inputMode="numeric"
                  value={draft}
                  disabled={!canEdit || patch.isPending}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setError(false);
                  }}
                  placeholder={t('settings.supplyIdealCoverage.placeholder')}
                  className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
              {canEdit ? (
                <button
                  type="button"
                  onClick={saveDays}
                  disabled={patch.isPending}
                  className="rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:bg-stocker-primary/90 disabled:opacity-50"
                >
                  {t('settings.supplyIdealCoverage.save')}
                </button>
              ) : null}
            </div>
            <p className="text-xs text-gray-500">{t('settings.supplyIdealCoverage.refreshHint')}</p>
          </div>
        )}
        {error ? (
          <p className="mt-2 text-xs text-red-600">{t('settings.supplyIdealCoverage.invalid')}</p>
        ) : null}
        {!canEdit ? (
          <p className="mt-2 text-xs text-gray-500">{t('settings.supplyIdealCoverage.readOnly')}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
